import { Roleavg } from "../components/Roles/Sidebar/RoleLeftSideBar";
import { AssessmentSlot, AutoAdvanceCondition, Candidate, Impact, InterviewSlots, Role, RoleAssessmentMapping, RoleStatus, RoleStatusCandidateCount, RoleStatusType, RoleType, RoleValidationConfig } from "../Models";
import { GetCandidateForRole } from "./candidates.api";
import { APIURL, paginationData } from "./config";
import { AvailableSlot } from "../components/Roles/RoleCandidates/BulkActionModalDescription";

// Interviewer type
export type Interviewer = {
  name: string;
  email: string;
};

export { GetCandidateForRole };

export type RolePromptConfigRecord = {
  id?: string;
  roleId: string;
  configName: string;
  description: string;
  configJson: string;
  generatedPrompt: string;
  version?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
};

export type RolePromptConfigValidationResponse = {
  isValid: boolean;
  errors: string[];
  warnings: string[];
};

export async function GetRoles(
  filterParams: Record<string, string[] | Roleavg[]>,
  sortType: string,
  sortDir: string,
  query: string,
  token: string,
  pageNumber: number = 1,
  pageSize: number = 15
): Promise<Role[]> {
  const res = await fetch(
    `${APIURL}/Roles/GetRoles?sortType=${sortType}&sortDir=${sortDir}&searchQuery=${query}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(filterParams),
    }
  );
  const roles: Role[] = await res.json();
  const totalCount = parseInt(res.headers.get("X-Total-Count") ?? "0", 10);

  (roles as any)._totalCount = totalCount;

  return roles;
}

export async function GetJustRoles() {
  const res = await fetch(
    `${APIURL}/Roles/JustRoles`);
  const roles: Role[] = await res.json();
  return roles;
}

export async function getAllRoles() {
  const res = await fetch(`${APIURL}/Roles/GetAllRoles`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  const roles: Role[] = await res.json();
  return roles;
}

export const checkAvailableSlots = async (roleId: string | undefined, token?: string) => {
  try {
    const headers: any = {
      "Content-Type": "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(
      `${APIURL}/Roles/checkAvailableSlots?roleId=${roleId}`,
      { headers }
    );
    if (!response.ok) {
      throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data: AvailableSlot[] = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching available slots:", error);
    throw error;
  }
};

export const getAssessmentVersions = async () => {
  try {
    const res = await fetch(`${APIURL}/MasterData/assessment-versions`);
    if (!res.ok) {
      // Endpoint not found or error - use defaults
      return [
        { id: 1, name: "Version 1 - Batch Submission" },
        { id: 2, name: "Version 2 - Sequential Submission" },
      ];
    }
    const data = await res.json();
    return data;
  } catch (error) {
    // Network error - use defaults silently
    return [
      { id: 1, name: "Version 1 - Batch Submission" },
      { id: 2, name: "Version 2 - Sequential Submission" },
    ];
  }
};

export async function GetRole(id: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const role: Role = await res.json();
  return role;
}

export async function CreateRole(body: Role, token: string) {
  const res = await fetch(`${APIURL}/Roles`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const role: Role = await res.json();
  return role;
}

export async function DeleteRole(roleId: string, token: string, deleteRole: boolean) {
  await fetch(`${APIURL}/Roles/${roleId}?deleteRole=${deleteRole}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function UpdateRole(
  roleId: string,
  body: { propertyName: string; propertyValue: string }[],
  token: string
) {
  await fetch(`${APIURL}/Roles/${roleId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function PutUpdateRole(roleId: string, body: Role, token: string) {
  await fetch(`${APIURL}/Roles/${roleId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function GetRolesPublic() {
  const res = await fetch(`${APIURL}/Roles/Public`);
  const roles: Role[] = await res.json();
  return roles;
}

export async function GetRolePublic(id: string) {
  const res = await fetch(`${APIURL}/Roles/Public/${id}`);
  const role: Role = await res.json();
  return role;
}

export async function GetRoleId(name: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/GetRoleByName?name=${name}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const role: Role = await res.json();
  return role;
}

export async function GetRoleStatuses(token: string) {
  const res = await fetch(`${APIURL}/RoleStatus`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const roleStatuses: RoleStatus[] = await res.json();
  return roleStatuses;
}

export async function GetRoleStatus(roleId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${roleId}/RoleStatus`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const roleStatus: RoleStatus[] = await res.json();
  return roleStatus;
}

export async function GetRoleStatusById(statusId: string, token: string): Promise<RoleStatus> {
  const res = await fetch(`${APIURL}/RoleStatus/${statusId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(res.statusText);
  }
  const roleStatus: RoleStatus = await res.json();
  return roleStatus;
}

export async function updateRoleAssessment(templateId: string, roleId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${roleId}/assessmentTemplate/${templateId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return res;
}
//
export async function GetStatusForRole(roleId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${roleId}/Status`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data: RoleStatusCandidateCount = await res.json();
  return data;
}

export async function CreateRoleStatus(roleId: string, body: RoleStatus, token: string) {
  await fetch(`${APIURL}/Roles/Status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function PostRoleStatus(body: RoleStatus, token: string) {
  await fetch(`${APIURL}/Roles/Status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function CreateMultipleRoleStatuses(
  roleId: string,
  statuses: RoleStatus[],
  token: string
) {
  await fetch(`${APIURL}/Roles/${roleId}/RoleStatus/CreateMultiple`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(statuses),
  });
}

export async function UpdateRoleStatus(
  roleStatusId: string,
  body: { propertyName: string; propertyValue: string }[],
  token: string
) {
  await fetch(`${APIURL}/Roles/Status/${roleStatusId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function UpdateRoleStatusFull(
  roleStatusId: string,
  token: string,
  body: RoleStatus
) {
  await fetch(`${APIURL}/Roles/Status/${roleStatusId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function DeleteRoleStatus(roleStatusId: string, token: string) {
  await fetch(`${APIURL}/RoleStatus/${roleStatusId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function GetRoleStatusTypes(token: string) {
  const res = await fetch(`${APIURL}/RoleStatusTypes`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const types: RoleStatusType[] = await res.json();
  return types;
}

export async function GetRoleStatusCandidateCount(roleId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${roleId}/RoleStatus/CandidateCount`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const counts: RoleStatusCandidateCount[] = await res.json();
  return counts;
}

export async function CreateAssessmentSlot(
  roleStatusId: string,
  assessmentSlot: AssessmentSlot,
  token: string
) {
  await fetch(`${APIURL}/RoleStatus/${roleStatusId}/AssessmentSlot`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(assessmentSlot),
  });
}

export async function CreateMultipleAssessmentSlots(
  roleStatusId: string,
  assessmentSlots: AssessmentSlot[],
  token: string
) {
  await fetch(`${APIURL}/RoleStatus/${roleStatusId}/AssessmentSlots/CreateMultiple`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(assessmentSlots),
  });
}

export async function UpdateAssessmentSlot(
  assessmentSlotId: string,
  body: { propertyName: string; propertyValue: string }[],
  token: string
) {
  await fetch(`${APIURL}/AssessmentSlot/${assessmentSlotId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function DeleteAssessmentSlot(assessmentSlotId: string, token: string) {
  await fetch(`${APIURL}/AssessmentSlot/${assessmentSlotId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function GetRoleAssessmentMappings(roleId: string, token: string) {
  const res = await fetch(`${APIURL}/RoleAssessmentMapping/role/${roleId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const roleAssessmentMappings: RoleAssessmentMapping[] = await res.json();
  return roleAssessmentMappings;
}

export async function CreateRoleAssessmentMapping(
  roleId: string,
  roleAssessmentMapping: RoleAssessmentMapping,
  token: string
) {
  await fetch(`${APIURL}/Roles/${roleId}/RoleAssessmentMappings`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(roleAssessmentMapping),
  });
}

export async function UpdateRoleAssessmentMapping(
  roleId: string,
  roleAssessmentMappingId: string,
  body: { propertyName: string; propertyValue: string }[],
  token: string
) {
  await fetch(`${APIURL}/Roles/${roleId}/RoleAssessmentMappings/${roleAssessmentMappingId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function DeleteRoleAssessmentMapping(
  roleAssessmentMappingId: string,
  token: string
) {
  await fetch(`${APIURL}/RoleAssessmentMappings/${roleAssessmentMappingId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function GetValidationConfig(roleId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/roleValidations/${roleId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const validationConfig: RoleValidationConfig = await res.json();
  return validationConfig;
}

export async function UpdateValidationConfig(
  roleId: string,
  validationConfig: RoleValidationConfig,
  token: string
) {
  await fetch(`${APIURL}/Roles/roleValidations/add/${roleId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(validationConfig),
  });
}

export const GetRoleValidationConfig = GetValidationConfig;
export const UpsertRoleValidationConfig = UpdateValidationConfig;
export const GetAssessmentRoleMapping = GetRoleAssessmentMappings;

export async function GetAutoAdvanceConditions(roleStatusId: string, token: string) {
  const res = await fetch(`${APIURL}/RoleStatus/${roleStatusId}/AutoAdvanceConditions`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const autoAdvanceConditions: AutoAdvanceCondition[] = await res.json();
  return autoAdvanceConditions;
}

export async function CreateAutoAdvanceCondition(
  roleStatusId: string,
  autoAdvanceCondition: AutoAdvanceCondition,
  token: string
) {
  await fetch(`${APIURL}/RoleStatus/${roleStatusId}/AutoAdvanceConditions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(autoAdvanceCondition),
  });
}

export async function PostAutoAdvanceCondition(autoAdvanceCondition: AutoAdvanceCondition, token: string) {
  await fetch(`${APIURL}/RoleStatus/${autoAdvanceCondition.roleStatusId}/AutoAdvanceConditions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(autoAdvanceCondition),
  });
}

export async function UpdateAutoAdvanceCondition(
  roleStatusId: string,
  autoAdvanceConditionId: string,
  body: { propertyName: string; propertyValue: string }[],
  token: string
) {
  await fetch(
    `${APIURL}/RoleStatus/${roleStatusId}/AutoAdvanceConditions/${autoAdvanceConditionId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    }
  );
}

export async function DeleteAutoAdvanceCondition(
  autoAdvanceConditionId: string,
  token: string
) {
  await fetch(`${APIURL}/AutoAdvanceConditions/${autoAdvanceConditionId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}

export const addAssessmentSlots = async (slots: AssessmentSlot[], token: string) => {
  const adjustedSlots = slots.map((slot) => ({
    ...slot,
    startTime: new Date(slot.startTime).toISOString(),
    endTime: new Date(slot.endTime).toISOString(),
  }));
  const res = await fetch(`${APIURL}/Roles/addAssessmentSlots`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(adjustedSlots),
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || "Failed to add assessment slots");
  }

  return res.json();
};
export async function getAssessmentSlotsByRole(roleId: string, token?: string) {
  const headers: any = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${APIURL}/Roles/${roleId}/AssessmentSlots`, {
    headers: headers,
  });
  const data = await res.json();
  return data;
}

// Interview Slots Functions
export const GetInterviewSlots = async (roleId: string, token: string) => {
  const url = `${APIURL}/Roles/interviewSlots?roleId=${roleId}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const slots: InterviewSlots[] = await res.json();
  return slots;
};

export const GetInterviewSlotById = async (id: string, token: string) => {
  const url = `${APIURL}/Roles/interviewSlots/${id}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const slots: InterviewSlots = await res.json();
  return slots;
};


export const GetCandidateInterviewNotes = async (
  candidateId: string,
  roleId: string,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/Roles/interviewSlots/candidate?candidateId=${candidateId}&roleId=${roleId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  if (!res.ok) {
    return;
  }
  const result = await res.json();
  return result;
};

export const GetRoleImpacts = async (roleId: string, token: string) => {
  const res = await fetch(`${APIURL}/Roles/${roleId}/impacts`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json: Impact[] = await res.json();
  return json;
};

export const UpsertRoleImpacts = async (
  roleId: string,
  body: Impact[],
  token: string
) => {
  const res = await fetch(`${APIURL}/Roles/${roleId}/impacts`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const json: Impact[] = await res.json();
  return json;
};


export const GetInterviewSlotsByMonth = async (
  token: string,
  year: number,
  month: number,
  roleId?: string | null
) => {
  const search = new URLSearchParams();
  search.set("year", String(year));
  search.set("month", String(month));
  if (roleId) search.set("roleId", roleId);

  const url = `${APIURL}/Roles/interviewSlots/byMonth?${search.toString()}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const slots: InterviewSlots[] = await res.json();
  return slots;
};

export const GetAvailableInterviewSlots = async (
  token: string,
  roleId?: string,
  roleStatusId?: string,
  fromDate?: string
) => {
  const params = new URLSearchParams();
  if (roleId) params.append("roleId", roleId);
  if (roleStatusId) params.append("roleStatusId", roleStatusId);
  if (fromDate) params.append("fromDate", fromDate);

  const queryString = params.toString();
  const url = `${APIURL}/Roles/availableInterviewSlots${queryString ? `?${queryString}` : ""}`;

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error("API Error:", errorText);
    throw new Error(errorText || "Failed to fetch available interview slots");
  }

  const slots: InterviewSlots[] = await res.json();
  return slots;
};

export type AvailableAssessmentSlot = {
  id: string;
  startTime: string;
  endTime: string;
  roleStatusId: string;
  roleStatusName: string;
  assessmentName: string | null;
  currentCandidateCount: number;
};

export const GetAvailableAssessmentSlots = async (
  roleId: string,
  token: string
): Promise<AvailableAssessmentSlot[]> => {
  const res = await fetch(`${APIURL}/Roles/availableAssessmentSlots?roleId=${roleId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to fetch available assessment slots");
  }

  return await res.json();
};

export const BookInterviewSlot = async (
  slotId: string,
  candidateId: string,
  notes: string | null,
  token: string
) => {
  const res = await fetch(`${APIURL}/Candidates/bookInterviewSlot`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      slotId,
      candidateId,
      notes,
    }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to book interview slot");
  }

  return await res.json();
};

export const UnbookInterviewSlot = async (slotId: string, token: string) => {
  const res = await fetch(`${APIURL}/Roles/unbookInterviewSlot/${slotId}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to unbook interview slot");
  }

  return await res.json();
};

export const UnbookInterviewSlots = async (slotIds: string[], token: string) => {
  const res = await fetch(`${APIURL}/Roles/unbookInterviewSlots`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ slotIds }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to unbook interview slots");
  }

  return await res.json();
};

export const createSlots = async (
  startTime: string,
  endTime: string,
  slotDuration: number,
  breakDuration: number,
  roleId: string | undefined,
  meetingLink: string,
  statusId: string,
  interviewerEmails: string[] | null,
  token: string
) => {
  const res = await fetch(`${APIURL}/Roles/createSlots`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      startTime,
      endTime,
      slotDurationMinutes: slotDuration,
      breakDurationMinutes: breakDuration,
      meetingLink,
      roleStatusId: statusId,
      interviewerEmail: interviewerEmails,
      roleId: roleId,
    }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to create slots");
  }

  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return await res.json();
  } else {
    return await res.text();
  }
};

// Get list of interviewers
export async function GetInterviewers(token: string): Promise<Interviewer[]> {
  try {
    const res = await fetch(`${APIURL}/Interviewers/list`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      console.error("Failed to fetch interviewers");
      return [];
    }

    const interviewers: Interviewer[] = await res.json();
    return interviewers;
  } catch (error) {
    console.error("Error fetching interviewers:", error);
    return [];
  }
}

export async function DeleteInterviewSlot(id: string, token: string) {
  const url = `${APIURL}/Roles/deleteSlot/${id}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to delete interview slot");
  }

  return res;
}

export async function DeleteInterviewSlots(slotIds: string[], token: string) {
  const url = `${APIURL}/Roles/deleteSlots`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ slotIds }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to delete interview slots");
  }

  return await res.json();
}

export async function AddRoleQuestion(roleId: string, questionId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${roleId}/questions/${questionId}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to map question to role");
  }

  return await res.json();
}

export async function DeleteRoleQuestion(roleId: string, questionId: string, token: string) {
  const res = await fetch(`${APIURL}/Roles/${roleId}/questions/${questionId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to delete question mapping from role");
  }

  return await res.json();
}

export interface UpdateInterviewSlotResult {
  slotId: string;
  candidateNotified: boolean;
  candidateNotificationFailed: boolean;
  interviewerNotified: boolean;
  interviewerNotificationFailed: boolean;
  notificationsSent: boolean;
}

export async function updateInterviewSlot(
  data: {
    slotId: string;
    startTime?: string;
    endTime?: string;
    meetingLink?: string;
    interviewerEmail?: string | null;
    notes?: string;
    sendNotifications?: boolean;
  },
  token: string
): Promise<UpdateInterviewSlotResult> {
  // Previously used /Roles/interviewSlots/${data.slotId} which doesn't exist —
  // the actual backend route is PUT /Roles/updateInterviewSlot (slotId in body).
  const url = `${APIURL}/Roles/updateInterviewSlot`;
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to update interview slot");
  }
  return await res.json() as UpdateInterviewSlotResult;
}

export async function refreshRoleCandidateCache(roleId: string, token: string) {
  const url = `${APIURL}/Roles/${roleId}/RefreshCandidateCache`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res;
}

export async function UpdateRoleQuestionShowInCandidateList(
  roleId: string,
  questionId: string,
  show: boolean,
  token: string
) {
  const res = await fetch(
    `${APIURL}/Roles/${roleId}/questions/${questionId}/show-in-candidate-list`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ show }),
    }
  );

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to update role question list visibility");
  }

  return await res.json();
}

export const GetRoleTypes = async () => {
  const res = await fetch(`${APIURL}/RoleType`);
  if (!res.ok) {
    throw new Error("Failed to fetch role types");
  }
  const roleTypes: RoleType[] = await res.json();
  return roleTypes;
}

export const CreateRoleType = async (
  name: string,
  token: string,
  isActive: boolean = true
) => {
  const res = await fetch(`${APIURL}/RoleType`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ name, isActive }),
  });

  if (!res.ok) {
    const message = await res.text();
    throw new Error(message || "Failed to create role type");
  }

  const roleType: RoleType = await res.json();
  return roleType;
};

export const DeleteRoleType = async (id: string, token: string) => {
  const res = await fetch(`${APIURL}/RoleType/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok && res.status !== 204) {
    const message = await res.text();
    throw new Error(message || "Failed to delete role type");
  }
};

export const GetAllQuestionForRole = async (roleId: string, token: string) => {
  const res = await fetch(`${APIURL}/Roles/${roleId}/questions`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch questions for role");
  }
  const questions = await res.json();
  return questions;
};

export async function GetRolePromptConfig(roleId: string, token: string): Promise<RolePromptConfigRecord> {
  const res = await fetch(`${APIURL}/Roles/${roleId}/promptConfig`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    if (res.status === 404) return {} as RolePromptConfigRecord;
    throw new Error(await res.text());
  }
  return await res.json();
}

export async function UpsertRolePromptConfig(roleId: string, data: RolePromptConfigRecord, token: string): Promise<RolePromptConfigRecord> {
  const res = await fetch(`${APIURL}/Roles/${roleId}/promptConfig`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return await res.json();
}
